import { AnalyticsInfo } from "store/adapter-config/AdapterConfigTypes";
import {
  PerformanceReportActionTypes,
  SET_PERFORMANCE_REPORT_DATE_RANGE,
  SET_PERFORMANCE_REPORT_STORE_ID,
  SetPerformanceReportDateRangeAction,
  SetPerformanceReportStoreIdAction,
  GET_REPORT_ANALYTICS_CONFIG_DETAILS,
  ReportAnalyticsConfigSuccessAction,
  FETCH_PERFORMANCE_METRICS,
  FetchPerformanceMetricsSuccessAction,
  FETCH_KPIS_PIE_CHART,
  FetchPieChartDataAction,
  MetricCategory,
  PerformanceMetricsResponse,
  FetchPerformanceMetricsAction,
  PieChartState,
  FetchPieChartDataSuccessAction,
  KPIViewProductsState,
  FETCH_KPI_PRODUCTS,
  FetchKPIProductsSuccessAction,
  SET_KPI_PRODUCTS_LOADING,
  KPIProductDetails,
  FETCH_ORDERS_BY_LOCATIONS,
  RESET_LOCATION_TREE_STATE,
  FetchLocationTreeDataAction,
  FetchLocationTreeDataFailureAction,
  LocationTreeCallApiSuccessAction,
  LocationTreeState,
  FETCH_KPI_ANALYSIS,
  FetchKPIAnalysisSuccessAction,
  FetchKPIAnalysisFailureAction,
  KPIAnalysisState,
  DateYearMetricsState,
  FETCH_DATE_YEAR_METRICS,
  FetchDateYearMetricsSuccessAction,
  FetchDateYearMetricsFailureAction,
  FetchDateYearMetricsAction,
  UpdateKPIProductsDetailsAction,
  UPDATE_KPI_PRODUCTS_DETAILS,
  FETCH_KPI_STORE_LIST,
  FetchKPIStoreListSuccessAction,
  KPIStoreListState,
} from "./PerformanceReportTypes";
import {
  createInitialMetricsState,
  processReportData,
  getChartPropertyName,
  transformApiResponseToNodes,
  updateNodeLoading,
  addChildrenToNode,
} from "utils/PerformanceReportUtils";

export interface LastFetchedParams {
  storeId: string;
  dateRange: string;
}

export interface PerformanceReportState {
  reportStoreId: string | null;
  reportDateRange: string;
  reportAnalyticsConfig: AnalyticsInfo;
  metrics: {
    [key in MetricCategory]: PerformanceMetricsResponse;
  };
  pieCharts: PieChartState;
  kpiViewProducts: KPIViewProductsState;
  isKPIProductsLoading: boolean;
  kpiProductDetails: KPIProductDetails[];
  locationTree: LocationTreeState;
  kpiAnalysis: KPIAnalysisState;
  dateYearMetrics: DateYearMetricsState;
  kpiStoreList: KPIStoreListState;
  lastFetchedParams: {
    [key in MetricCategory]?: LastFetchedParams;
  };
}

const initialState: PerformanceReportState = {
  reportStoreId: null,
  reportDateRange: "",
  reportAnalyticsConfig: {
    adapterId: "",
    capabilities: [],
    operations: [],
  },
  metrics: createInitialMetricsState(),
  pieCharts: {},
  kpiViewProducts: {
    data: null,
  },
  kpiProductDetails: [],
  isKPIProductsLoading: false,
  locationTree: {
    data: [],
    isLoading: false,
    expandedNodes: [],
  },
  kpiAnalysis: {
    data: null,
    isLoading: false,
    retryAfter: null,
    is404Error: false,
    shouldDisableSummaryTab: true,
  },
  dateYearMetrics: {},
  kpiStoreList: {
    stores: [],
    isLoading: false,
    isLoaded: false,
  },
  lastFetchedParams: {},
};

export const PerformanceReportReducer = (
  state = initialState,
  action: PerformanceReportActionTypes,
): PerformanceReportState => {
  switch (action.type) {
    case SET_PERFORMANCE_REPORT_STORE_ID: {
      const reportStoreIdAction = action as SetPerformanceReportStoreIdAction;
      return {
        ...state,
        locationTree: {
          ...state.locationTree,
          data: [],
          isLoading: false,
          expandedNodes: [],
        },
        reportAnalyticsConfig: {
          adapterId: "",
          capabilities: [],
          operations: [],
        },
        metrics: createInitialMetricsState(),
        pieCharts: {
          trafficBySource: {
            data: null,
            isLoading: false,
          },
          trafficByDevice: {
            data: null,
            isLoading: false,
          },
          ordersBySource: {
            data: null,
            isLoading: false,
          },
          ordersByDevice: {
            data: null,
            isLoading: false,
          },
        },
        kpiViewProducts: {
          data: null,
        },
        kpiProductDetails: [],
        isKPIProductsLoading: false,
        kpiAnalysis: {
          ...state.kpiAnalysis,
          data: null,
          isLoading: false,
          retryAfter: null,
          shouldDisableSummaryTab: true,
        },
        dateYearMetrics: {},
        reportStoreId: reportStoreIdAction.payload.storeId,
        lastFetchedParams: {},
      };
    }
    case SET_PERFORMANCE_REPORT_DATE_RANGE: {
      const reportDateRangeAction =
        action as SetPerformanceReportDateRangeAction;
      return {
        ...state,
        locationTree: {
          ...state.locationTree,
          data: [],
          isLoading: false,
          expandedNodes: [],
        },
        reportDateRange: reportDateRangeAction.payload.dateRange,
        lastFetchedParams: {},
      };
    }
    case GET_REPORT_ANALYTICS_CONFIG_DETAILS.SUCCESS: {
      const reportAnalyticsConfigAction =
        action as ReportAnalyticsConfigSuccessAction;
      return {
        ...state,
        reportAnalyticsConfig: reportAnalyticsConfigAction.payload,
      };
    }
    case GET_REPORT_ANALYTICS_CONFIG_DETAILS.FAILURE:
      return {
        ...state,
        reportAnalyticsConfig: {
          adapterId: "",
          capabilities: [],
          operations: [],
        },
      };

    case FETCH_PERFORMANCE_METRICS.REQUEST: {
      const requestAction = action as FetchPerformanceMetricsAction;
      const { category } = requestAction.payload;
      return {
        ...state,
        metrics: {
          ...state.metrics,
          [category]: {
            ...state.metrics[category],
            isLoading: true,
          },
        },
      };
    }

    case FETCH_PERFORMANCE_METRICS.SUCCESS: {
      const successAction = action as FetchPerformanceMetricsSuccessAction;
      const {
        category: successCategory,
        currentData,
        comparisonData,
        metrics: requestedMetrics,
      } = successAction.payload;

      const processedMetrics = processReportData(
        currentData,
        comparisonData,
        requestedMetrics,
      );
      return {
        ...state,
        metrics: {
          ...state.metrics,
          [successCategory]: {
            category: successCategory,
            metrics: processedMetrics,
            isLoading: false,
          },
        },
        lastFetchedParams: {
          ...state.lastFetchedParams,
          [successCategory]: {
            storeId: state.reportStoreId || "",
            dateRange: state.reportDateRange,
          },
        },
      };
    }

    case FETCH_PERFORMANCE_METRICS.FAILURE: {
      const failureAction = action as any;
      const failureCategory = failureAction?.payload?.category;
      if (!failureCategory) {
        return state;
      }
      return {
        ...state,
        metrics: {
          ...state.metrics,
          [failureCategory]: {
            ...state.metrics[failureCategory],
            isLoading: false,
          },
        },
      };
    }
    case FETCH_KPIS_PIE_CHART.REQUEST: {
      const pieChartRequestAction = action as FetchPieChartDataAction;
      const requestKey =
        pieChartRequestAction.payload.metricId ||
        (pieChartRequestAction.payload.chartType
          ? getChartPropertyName(pieChartRequestAction.payload.chartType)
          : "unknown");
      return {
        ...state,
        pieCharts: {
          ...state.pieCharts,
          [requestKey]: {
            ...(state.pieCharts[requestKey] || { data: null }),
            isLoading: true,
          },
        },
      };
    }
    case FETCH_KPIS_PIE_CHART.SUCCESS: {
      const pieChartSuccessAction = action as FetchPieChartDataSuccessAction;
      const successKey =
        pieChartSuccessAction.payload.metricId ||
        (pieChartSuccessAction.payload.chartType
          ? getChartPropertyName(pieChartSuccessAction.payload.chartType)
          : "unknown");
      const apiResponseData = {
        dimensionHeader: pieChartSuccessAction.payload.dimensionHeader,
        metricHeaders: pieChartSuccessAction.payload.metricHeaders,
        rows: pieChartSuccessAction.payload.rows,
      };
      return {
        ...state,
        pieCharts: {
          ...state.pieCharts,
          [successKey]: {
            data: apiResponseData,
            isLoading: false,
          },
        },
      };
    }
    case FETCH_KPIS_PIE_CHART.FAILURE:
      const failureMeta = (action as any).meta || {};
      const failureKey =
        failureMeta.metricId ||
        (failureMeta.chartType
          ? getChartPropertyName(failureMeta.chartType)
          : null);
      if (failureKey) {
        return {
          ...state,
          pieCharts: {
            ...state.pieCharts,
            [failureKey]: {
              data: null,
              isLoading: false,
            },
          },
        };
      }
      return state;
    case FETCH_KPI_PRODUCTS.REQUEST:
      return {
        ...state,
        kpiViewProducts: { data: null },
        isKPIProductsLoading: true,
      };
    case FETCH_KPI_PRODUCTS.SUCCESS: {
      const kpiProductsSuccessAction = action as FetchKPIProductsSuccessAction;
      return {
        ...state,
        kpiViewProducts: {
          data: kpiProductsSuccessAction.payload,
        },
        kpiProductDetails:
          kpiProductsSuccessAction.payload.rows?.map((product: any) => ({
            productId: product.dimensionValue,
            code: product.dimensionValue,
            analyticsId: product.dimensionValue,
          })) ?? [],
      };
    }
    case FETCH_KPI_PRODUCTS.FAILURE:
      return {
        ...state,
        kpiViewProducts: {
          ...state.kpiViewProducts,
        },
        isKPIProductsLoading: false,
      };
    case SET_KPI_PRODUCTS_LOADING:
      return {
        ...state,
        isKPIProductsLoading: false,
      };

    case FETCH_ORDERS_BY_LOCATIONS.REQUEST: {
      const requestAction = action as FetchLocationTreeDataAction;
      const { parentNodeId } = requestAction.payload;
      if (parentNodeId) {
        return {
          ...state,
          locationTree: {
            ...state.locationTree,
            data: updateNodeLoading(
              state.locationTree.data,
              parentNodeId,
              true,
            ),
          },
        };
      } else {
        return {
          ...state,
          locationTree: {
            ...state.locationTree,
            isLoading: true,
          },
        };
      }
    }
    case FETCH_ORDERS_BY_LOCATIONS.SUCCESS: {
      const successAction = action as LocationTreeCallApiSuccessAction;
      const response = successAction.payload;
      const dimension = response.dimension?.id;
      const parentNodeId = response.parentNodeId;
      const parentPath = response.dimensionBreakdown || [];
      const newNodes = transformApiResponseToNodes(
        response,
        dimension,
        parentPath,
      );

      if (parentNodeId) {
        return {
          ...state,
          locationTree: {
            ...state.locationTree,
            data: addChildrenToNode(
              state.locationTree.data,
              parentNodeId,
              newNodes,
            ),
            isLoading: false,
          },
        };
      } else {
        return {
          ...state,
          locationTree: {
            ...state.locationTree,
            data: newNodes,
            isLoading: false,
          },
        };
      }
    }
    case FETCH_ORDERS_BY_LOCATIONS.FAILURE: {
      const failureAction = action as FetchLocationTreeDataFailureAction;
      const parentNodeId = failureAction?.payload?.parentNodeId;
      if (parentNodeId) {
        return {
          ...state,
          locationTree: {
            ...state.locationTree,
            data: updateNodeLoading(
              state.locationTree.data,
              parentNodeId,
              false,
            ),
          },
        };
      } else {
        return {
          ...state,
          locationTree: {
            ...state.locationTree,
            isLoading: false,
          },
        };
      }
    }
    case RESET_LOCATION_TREE_STATE: {
      return {
        ...state,
        locationTree: {
          data: [],
          isLoading: false,
          expandedNodes: [],
        },
      };
    }
    case FETCH_KPI_ANALYSIS.REQUEST: {
      return {
        ...state,
        kpiAnalysis: {
          ...state.kpiAnalysis,
          isLoading: true,
          retryAfter: null,
          is404Error: false,
          shouldDisableSummaryTab: true,
        },
      };
    }
    case FETCH_KPI_ANALYSIS.SUCCESS: {
      const successAction = action as FetchKPIAnalysisSuccessAction;
      return {
        ...state,
        kpiAnalysis: {
          data: successAction.payload,
          isLoading: false,
          retryAfter: null,
          is404Error: false,
          shouldDisableSummaryTab: false,
        },
      };
    }
    case FETCH_KPI_ANALYSIS.FAILURE: {
      const failureAction = action as FetchKPIAnalysisFailureAction;
      const retryAfter = failureAction.payload?.retryAfter;
      const is404Error = failureAction.payload?.is404Error;
      const isRetryable = retryAfter != null && retryAfter > 0;
      return {
        ...state,
        kpiAnalysis: {
          ...state.kpiAnalysis,
          isLoading: isRetryable,
          retryAfter: retryAfter || null,
          is404Error: is404Error ?? false,
          shouldDisableSummaryTab: is404Error ?? false,
        },
      };
    }
    case FETCH_DATE_YEAR_METRICS.REQUEST: {
      const dateYearMetricsRequestAction = action as FetchDateYearMetricsAction;
      const requestCategory = dateYearMetricsRequestAction.payload.category;
      return {
        ...state,
        dateYearMetrics: {
          ...state.dateYearMetrics,
          [requestCategory]: {
            ...state.dateYearMetrics[requestCategory],
            isLoading: true,
          },
        },
      };
    }
    case FETCH_DATE_YEAR_METRICS.SUCCESS: {
      const dateYearMetricsSuccessAction =
        action as FetchDateYearMetricsSuccessAction;
      const { category: successCategory, ...responseData } =
        dateYearMetricsSuccessAction.payload;
      // Extract API response fields (rows, metricHeaders) from the merged payload
      const apiData = {
        rows: responseData.rows,
        metricHeaders: responseData.metricHeaders,
      };
      return {
        ...state,
        dateYearMetrics: {
          ...state.dateYearMetrics,
          [successCategory]: {
            data: apiData,
            isLoading: false,
          },
        },
      };
    }
    case FETCH_DATE_YEAR_METRICS.FAILURE: {
      const dateYearMetricsFailureAction =
        action as FetchDateYearMetricsFailureAction;
      const failureCategory = dateYearMetricsFailureAction.payload?.category;
      if (!failureCategory) {
        return state;
      }
      return {
        ...state,
        dateYearMetrics: {
          ...state.dateYearMetrics,
          [failureCategory]: {
            data: null,
            isLoading: false,
          },
        },
      };
    }
    case UPDATE_KPI_PRODUCTS_DETAILS: {
      const updateKPIProductsDetailsAction =
        action as UpdateKPIProductsDetailsAction;
      const products = updateKPIProductsDetailsAction.payload.products;

      // Create a map of analyticsId to productId for quick lookup
      const analyticsIdToProductIdMap = new Map(
        products.map((product) => [product.analyticsId, product.productId]),
      );

      // Update existing products or add new ones
      const updatedKpiProductDetails = state.kpiProductDetails.map(
        (existingProduct) => {
          // If analyticsId exists in the incoming products, update the productId
          if (analyticsIdToProductIdMap.has(existingProduct.analyticsId)) {
            return {
              ...existingProduct,
              productId: analyticsIdToProductIdMap.get(
                existingProduct.analyticsId,
              )!,
            };
          }
          return existingProduct;
        },
      );

      // Update kpiViewProducts data rows if it exists
      const updatedKpiViewProducts = state.kpiViewProducts.data
        ? {
            ...state.kpiViewProducts,
            data: {
              ...state.kpiViewProducts.data,
              rows:
                state.kpiViewProducts.data.rows?.map((row) => {
                  // Update dimensionValue if it matches an analyticsId
                  if (analyticsIdToProductIdMap.has(row.dimensionValue)) {
                    return {
                      ...row,
                      dimensionValue: analyticsIdToProductIdMap.get(
                        row.dimensionValue,
                      )!,
                    };
                  }
                  return row;
                }) ?? [],
            },
          }
        : state.kpiViewProducts;

      return {
        ...state,
        kpiProductDetails: updatedKpiProductDetails,
        kpiViewProducts: updatedKpiViewProducts,
      };
    }
    case FETCH_KPI_STORE_LIST.REQUEST:
      return {
        ...state,
        kpiStoreList: {
          ...state.kpiStoreList,
          isLoading: true,
        },
      };
    case FETCH_KPI_STORE_LIST.SUCCESS: {
      const kpiStoreListSuccessAction =
        action as FetchKPIStoreListSuccessAction;
      const kpiStores =
        kpiStoreListSuccessAction.payload.results?.map((e: any) => ({
          storeId: e.storeId,
          storeCode: e.code,
          storeName: e.name,
        })) ?? [];
      return {
        ...state,
        kpiStoreList: {
          stores: kpiStores,
          isLoading: false,
          isLoaded: true,
        },
      };
    }
    case FETCH_KPI_STORE_LIST.FAILURE:
      return {
        ...state,
        kpiStoreList: {
          ...state.kpiStoreList,
          isLoading: false,
          isLoaded: true,
        },
      };
    default:
      return state;
  }
};
